import { useCallback, useEffect, useState } from 'react';
import saveAs from 'file-saver';

import { DocumentPreviewDialog } from '@/components/pages/documents/document-preview-dialog';
import type { Document } from '@/modules/documents/domain/entities/document';
import { DownloadDocument } from '@/modules/documents/view/mutations/download-document';
import { toastResult } from '@/utils/toast';

export type DocumentPreviewProps = {
  document?: Document;
  onClose?: () => void;
};
export function DocumentPreview({ document, onClose }: DocumentPreviewProps) {
  const { mutateAsync: downloadDocument } = DownloadDocument.useMutation();
  const [previewFile, setPreviewFile] = useState<File | undefined>();

  const previewOrDownload = useCallback(
    async function (document: Document) {
      const result = await toastResult(downloadDocument({ document }), {
        success: undefined,
        loading: 'Downloading the file',
        error: (err) =>
          ({
            NOT_FOUND: "The file doesn't exist anymore",
          })[err.type],
        exception: 'An error occurred, please try again.',
      });

      if (result.isOk()) {
        setPreviewFile(result.value.file);
      }
    },
    [downloadDocument, setPreviewFile],
  );

  function handleFileNotSupported(file: File) {
    saveAs(file);
  }

  useEffect(
    function () {
      if (document) {
        previewOrDownload(document);
      }
    },
    [document, previewOrDownload],
  );

  return document ? (
    <DocumentPreviewDialog
      open={Boolean(previewFile)}
      file={previewFile}
      document={{ name: document.name, extension: document.file.extension }}
      onClose={function () {
        setPreviewFile(undefined);
        onClose?.();
      }}
      onFileNotSupported={handleFileNotSupported}
    />
  ) : null;
}
